/**
 * Copyright 2022 Phenix Real Time Solutions, Inc. All Rights Reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {h, Component} from 'preact'; // eslint-disable-line no-unused-vars
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {setStreamAction, setRendererAction, resetStreamAction} from '../actions/stream';
import {updateLatencyAction} from '../actions/statistics';
import {updatePauseStateAction, updateMuteStateAction, changeVideoEnabledStateAction, getAndSetBufferedDetails} from '../actions/video';

class StreamManager extends Component {
  setInitialState(prevProps) {
    this.props.setStream(this.props.stream);
    this.props.setRenderer(this.props.renderer);
    this.props.updateLatency((this.renderer && this.renderer.getStats().lag.toFixed(2)) || 0);

    if (prevProps.video !== this.props.video) {
      if (this.timeupdateListener) {
        this.props.video.removeEventListener('timeupdate', this.timeupdateListener);
        this.props.video.removeEventListener('play', this.playListener);
        this.props.video.removeEventListener('pause', this.pauseListener);
      }

      this.timeupdateListener = () => this.debouncedHandleTimeUpdate();
      this.playListener = () => {
        this.props.updateMuteState();
        this.props.updatePauseState();
      };
      this.pauseListener = () => {
        this.props.updatePauseState();
      };

      if (this.props.video.isLiveOrRtmpStream()) {
        this.props.video.addEventListener('timeupdate', this.timeupdateListener);
      }

      this.props.video.addEventListener('play', this.playListener);
      this.props.video.addEventListener('pause', this.pauseListener);

      this.props.updateMuteState();
      this.props.changeVideoEnabledState(this.props.video.getVideoState());
      this.props.updatePauseState();
    }
  }

  debouncedHandleTimeUpdate() {
    this.debounce(() => this.handleTimeUpdate(), 500);
  }

  debounce(callback, timeout) {
    if (this.lastTimeCalled + timeout > Date.now()) {
      return;
    }

    this.lastTimeCalled = Date.now();

    return callback();
  }

  handleTimeUpdate() {
    this.props.getAndSetBufferedDetails(this.props.renderer);
  }

  componentDidUpdate(prevProps) {
    this.setInitialState(prevProps);

    this.lastTimeCalled = 0;
    this.timeupdateListener = null;
    this.playListener = null;
    this.pauseListener = null;
  }

  componentWillUnmount() {
    this.props.resetStream();

    if (this.props.video && this.timeupdateListener) {
      this.props.video.removeEventListener('timeupdate', this.timeupdateListener);
      this.props.video.removeEventListener('play', this.playListener);
      this.props.video.removeEventListener('pause', this.pauseListener);
    }

    this.timeupdateListener = null;
  }

  render() {
    return null;
  }
}

StreamManager.propTypes = {
  stream: PropTypes.object,
  renderer: PropTypes.object,
  video: PropTypes.object,

  updateMuteState: PropTypes.func.isRequired,
  updatePauseState: PropTypes.func.isRequired,
  changeVideoEnabledState: PropTypes.func.isRequired,
  getAndSetBufferedDetails: PropTypes.func.isRequired,
  resetStream: PropTypes.func.isRequired,
  setRenderer: PropTypes.func.isRequired,
  setStream: PropTypes.func.isRequired,
  updateLatency: PropTypes.func.isRequired
};

const mapStateToProps = (state, {video}) => {
  return {
    stream: video ? video.getStream() : null,
    renderer: video ? video.getRenderer() : null,
    video
  };
};

const mapDispatchToProps = (dispatch, {video}) => {
  return {
    updateMuteState: () => dispatch(updateMuteStateAction(video)),
    updatePauseState: () => dispatch(updatePauseStateAction(video)),
    changeVideoEnabledState: (videoState) => dispatch(changeVideoEnabledStateAction(video, videoState)),
    getAndSetBufferedDetails: (renderer) => dispatch(getAndSetBufferedDetails(video, renderer)),
    resetStream: () => dispatch(resetStreamAction()),
    setRenderer: (renderer) => dispatch(setRendererAction(renderer)),
    setStream: (stream) => dispatch(setStreamAction(stream)),
    updateLatency: (latency) => dispatch(updateLatencyAction(latency))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(StreamManager);